import { Label, LabelData, LabelWidgetSettings, LabelType, LabelWidget } from '../Output/Label';

// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { DateFormat } from '@Utils/Formats';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import type { LocalizationMap } from '@Primitive/Localization/Localization';
// =======================================================================================================
// Label Description interface
export class LabelSingletonWidget extends IWidgetThingsClass<LabelData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <Label />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <LabelWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        _.forEach(wData.Labels, (label, index) => {
            thing.AddPort(new WebThingPort(
                {
                    Name: label.Value,
                    PortKey: label.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Label",
                    State: "Value"
                },
                WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        _.forEach(wData.Labels, (label, index) => {
            ports.push(new WebThingPort(
                {
                    Name: label.Value,
                    PortKey: label.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "Label",
                    State: "Value"
                },
                WebThingPortType.Data
            ));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): LabelData {
        return _.cloneDeep({
            Labels: [{ Key: "1_" + getRandomSmallKey(), Value: "Label 1", type: LabelType.text }],
            FillWidget: false,
            IsSingleton: true,
            dateFormat: DateFormat.Short,
            EvenRowBackgroundColor: '#7fdde9',
            OddRowBackgroundColor: '#ffffff'
        });
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return LabelSingletonWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: LabelData = LabelSingletonWidget.GetDefaultWidgetDataStatic();
        return {
            Title: "Label",
            WidgetTitle: "",
            Key: "LabelSingletonWidget",
            Type: "LabelSingletonWidget",
            Data: data,
            Description: "Label singleton widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            DefiesGrid: true,
            WidgetNoBackground: true,
            IconURI: WidgetsThingsStructs.IconURIPath + "/svg/labels.svg"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: LabelData): LabelData {
        data = LabelWidget.MigrateDefaultWidgetDataStatic(data);
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = LabelWidget.GetLocalizationDictionaryStatic(this.WidgetData);

        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================